import React, { useState } from 'react';
import { CheckCircle2, ChevronRight, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '@/components/ui/Button';
import AnimatedCard from '@/components/ui/AnimatedCard';
import Calendar from '@/components/booking/Calendar';
import TimeSelector from '@/components/booking/TimeSelector';
import ContactForm from '@/components/booking/ContactForm';
import StepIndicator from '@/components/booking/StepIndicator';
import { sendBookingEmail } from '@/service/emailService';
import { toast } from 'sonner';
import { clearAvailabilityCache } from '@/service/bookingService';
const BookingModule = ({ projectType }: { projectType?: string }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: projectType || '',
    message: '',
    date: '',
    time: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNextStep = () => {
    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handlePrevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Only submit if we're on the last step and all required fields are filled
    if (step === 3 && formData.name && formData.email && formData.phone && formData.projectType) {
      setIsLoading(true);
      try {
        await sendBookingEmail(formData);
        clearAvailabilityCache();
        setIsSubmitted(true);
        toast.success('Votre demande de rendez-vous a été envoyée avec succès!');
      } catch (error) {
        console.error('Error submitting form:', error);
        toast.error('Une erreur est survenue. Veuillez réessayer.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <section id="book" className="py-20 md:py-28 bg-gray-50">
      <div className="container px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="mt-4 text-3xl md:text-4xl font-bold">
            Discutons de votre <span className="text-orcsys-500">projet</span>
          </h2>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
            Prenez un rendez-vous téléphonique avec notre équipe pour discuter de vos besoins et comment nous pouvons vous aider.
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <AnimatedCard className="p-8">
            {!isSubmitted ? (
              <>
                <StepIndicator currentStep={step} />

                <form onSubmit={handleSubmit}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="relative"
                    >
                      {step === 1 && (
                        <div className="space-y-6">
                          <Calendar
                            currentMonth={currentMonth}
                            selectedDate={formData.date}
                            onDateSelect={(date) => setFormData({ ...formData, date })}
                            onMonthChange={setCurrentMonth}
                          />
                        </div>
                      )}

                      {step === 2 && (
                        <div className="space-y-6">
                          <TimeSelector
                            selectedDate={formData.date}
                            selectedTime={formData.time}
                            onTimeSelect={(time) => setFormData({ ...formData, time })}
                          />
                        </div>
                      )}

                      {step === 3 && (
                        <ContactForm
                          formData={formData}
                          onInputChange={handleInputChange}
                        />
                      )}
                    </motion.div>
                  </AnimatePresence>

                  <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8">
                    {step > 1 && (
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={handlePrevStep}
                        disabled={isLoading}
                        className="flex items-center justify-center gap-2 hover:scale-105 transition-transform w-full sm:w-auto"
                      >
                        <ChevronRight className="w-4 h-4 rotate-180" />
                        Précédent
                      </Button>
                    )}
                    <div className={`${step === 1 ? 'w-full' : 'w-full sm:w-auto'}`}>
                      {step < 3 ? (
                        <Button 
                          type="button" 
                          onClick={handleNextStep} 
                          disabled={
                            isLoading ||
                            (step === 1 && !formData.date) ||
                            (step === 2 && !formData.time)
                          }
                          className="flex items-center justify-center gap-2 hover:scale-105 transition-transform w-full"
                        >
                          Suivant
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      ) : (
                        <Button 
                          type="submit" 
                          disabled={isLoading || !formData.name || !formData.email || !formData.phone || !formData.projectType}
                          className="hover:scale-105 transition-transform w-full"
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Envoi en cours...
                            </>
                          ) : (
                            'Confirmer le rendez-vous'
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </form>
              </>
            ) : (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 bg-orcsys-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 size={32} className="text-orcsys-500" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Rendez-vous planifié!</h3>
                <p className="text-gray-600 mb-6">
                  Merci d'avoir pris rendez-vous avec nous. Un courriel de confirmation sera envoyé à {formData.email}.
                </p>
                <Button 
                  type="button" 
                  onClick={() => {
                    setIsSubmitted(false);
                    setStep(1);
                    setFormData({
                      name: '',
                      email: '',
                      phone: '',
                      company: '',
                      projectType: '',
                      message: '',
                      date: '',
                      time: ''
                    });
                  }}
                  variant="outline"
                  className="hover:scale-105 transition-transform w-full sm:w-auto"
                >
                  Planifier un autre rendez-vous
                </Button>
              </motion.div>
            )}
          </AnimatedCard>
        </div>
      </div>
    </section>
  );
};

export default BookingModule;
